import { useEffect, useState } from 'react';
import { useAppStore } from '@/stores/appStore';
import { API_CONFIG, fetchAPI } from '@/services/api';

export function PaymentResults() {
  const userEmail = useAppStore((state) => state.userEmail);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payments, setPayments] = useState<any[]>([]);

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!userEmail) return;
      setLoading(true);
      setError(null);
      try {
        const res = await fetchAPI<any>(
          `${API_CONFIG.USERS_API}/payments/results?email=${encodeURIComponent(userEmail)}`
        );
        if (!mounted) return;
        if (!res?.success) {
          setError(res?.error || 'Failed to load payment results');
          setPayments([]);
          return;
        }
        setPayments(res?.payments ?? []);
      } catch (err: any) {
        if (!mounted) return;
        setError(err?.message || 'Failed to load payment results');
        setPayments([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, [userEmail]);

  return (
    <div className="payment-results-page min-h-screen bg-dark-bg p-6 text-white">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold">Payment Results</h1>
          <p className="text-gray-400 text-sm">Internal payment webhook log (admin-only).</p>
        </div>

        {loading && (
          <div className="payment-surface bg-gray-900/60 border border-gray-700 rounded-lg p-4 text-sm text-gray-300">
            Loading payment results…
          </div>
        )}

        {!loading && error && (
          <div className="payment-surface bg-red-900/40 border border-red-700 rounded-lg p-4 text-sm text-red-200">
            {error}
          </div>
        )}

        {!loading && !error && payments.length === 0 && (
          <div className="payment-surface bg-gray-900/60 border border-gray-700 rounded-lg p-4 text-sm text-gray-300">
            No payment events yet.
          </div>
        )}

        {!loading && !error && payments.length > 0 && (
          <div className="payment-table-wrap overflow-auto border border-gray-800 rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-900 text-gray-300">
                <tr>
                  <th className="text-left px-4 py-3">Created</th>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Paid At</th>
                  <th className="text-left px-4 py-3">Payment ID</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Amount</th>
                  <th className="text-left px-4 py-3">Currency</th>
                  <th className="text-left px-4 py-3">Invoice</th>
                  <th className="text-left px-4 py-3">Product</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p, idx) => (
                  <tr key={`${p.createdAtPaymentId ?? idx}`} className="border-t border-gray-800">
                    <td className="px-4 py-3 text-gray-300">{p.createdAt ?? '-'}</td>
                    <td className="px-4 py-3 text-gray-200">{p.payerName ?? '-'}</td>
                    <td className="px-4 py-3 text-gray-200">{p.payerEmail ?? '-'}</td>
                    <td className="px-4 py-3 text-gray-200">{p.paidAt ?? '-'}</td>
                    <td className="px-4 py-3 text-gray-200">{p.paymentId ?? '-'}</td>
                    <td className="px-4 py-3 text-gray-200">{p.status ?? '-'}</td>
                    <td className="px-4 py-3 text-gray-200">
                      {typeof p.amount === 'number' ? p.amount.toLocaleString() : '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-200">{p.currency ?? '-'}</td>
                    <td className="px-4 py-3 text-gray-200">{p.invoiceId ?? '-'}</td>
                    <td className="px-4 py-3 text-gray-200">{p.productSummary ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
